import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Tv, Megaphone, Layers, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

const AD_SERVERS = [
  { value: "fast_tv_ads", label: "Aniview (FAST TV Ads)", icon: Megaphone },
  { value: "springserve", label: "SpringServe", icon: Tv },
];

const fmtNum = (n) => (n === null || n === undefined || n === "" ? "∞" : Number(n).toLocaleString());

function emptyTiers() {
  return [{ from_impressions: 0, to_impressions: 1000000, cpm: "" }];
}

export default function AdServerCosts({ api = adminApi, readOnly = false }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ad_server: "fast_tv_ads", start_date: "", end_date: "", tiers: emptyTiers() });

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getAdCosts();
      setConfigs(res.data);
    } catch {
      toast.error("Failed to load ad server costs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ad_server: "fast_tv_ads", start_date: "", end_date: "", tiers: emptyTiers() });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      ad_server: c.ad_server,
      start_date: c.start_date,
      end_date: c.end_date || "",
      tiers: c.tiers.map((t) => ({ from_impressions: t.from_impressions, to_impressions: t.to_impressions, cpm: t.cpm })),
    });
    setDialogOpen(true);
  };

  // ---- Tier editing ----
  const addTier = () => {
    setForm((f) => {
      const tiers = [...f.tiers];
      const last = tiers[tiers.length - 1];
      const newFrom = last.to_impressions ?? (Number(last.from_impressions) + 1000000);
      // ensure previous tier is bounded
      if (last.to_impressions === null || last.to_impressions === "") {
        last.to_impressions = newFrom;
      }
      tiers.push({ from_impressions: newFrom, to_impressions: null, cpm: "" });
      return { ...f, tiers };
    });
  };

  const removeTier = (idx) => {
    setForm((f) => {
      if (f.tiers.length === 1) return f;
      const tiers = f.tiers.filter((_, i) => i !== idx);
      // keep contiguity: each tier.from = prev.to; first = 0
      tiers[0].from_impressions = 0;
      for (let i = 1; i < tiers.length; i++) tiers[i].from_impressions = tiers[i - 1].to_impressions ?? tiers[i].from_impressions;
      tiers[tiers.length - 1].to_impressions = tiers[tiers.length - 1].to_impressions; // unchanged
      return { ...f, tiers };
    });
  };

  const updateTierTo = (idx, value) => {
    setForm((f) => {
      const tiers = f.tiers.map((t) => ({ ...t }));
      tiers[idx].to_impressions = value === "" ? null : Number(value);
      if (idx + 1 < tiers.length) tiers[idx + 1].from_impressions = tiers[idx].to_impressions ?? tiers[idx + 1].from_impressions;
      return { ...f, tiers };
    });
  };

  const updateTierCpm = (idx, value) => {
    setForm((f) => {
      const tiers = f.tiers.map((t) => ({ ...t }));
      tiers[idx].cpm = value;
      return { ...f, tiers };
    });
  };

  const handleSave = async () => {
    if (!form.start_date) { toast.error("Start date is required"); return; }
    const tiers = form.tiers.map((t) => ({
      from_impressions: Number(t.from_impressions),
      to_impressions: t.to_impressions === null || t.to_impressions === "" ? null : Number(t.to_impressions),
      cpm: t.cpm === "" ? null : Number(t.cpm),
    }));
    if (tiers.some((t) => t.cpm === null || isNaN(t.cpm))) { toast.error("Every tier needs a CPM (enter 0 if free)"); return; }
    setSaving(true);
    try {
      const payload = { ad_server: form.ad_server, start_date: form.start_date, end_date: form.end_date || null, tiers };
      if (editing) {
        await adminApi.updateAdCost(editing.cost_id, { start_date: payload.start_date, end_date: payload.end_date, tiers });
        toast.success("Slab updated");
      } else {
        await adminApi.createAdCost(payload);
        toast.success("Slab created");
      }
      setDialogOpen(false);
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save slab");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await adminApi.deleteAdCost(deleteTarget.cost_id);
      toast.success("Slab deleted");
      setDeleteTarget(null);
      fetchConfigs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const serverLabel = (v) => AD_SERVERS.find((s) => s.value === v)?.label || v;
  const grouped = AD_SERVERS.map((s) => ({ ...s, items: configs.filter((c) => c.ad_server === s.value) }));

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="ad-server-costs-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Ad Server Costs
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Slab-wise CPM the ad servers charge CloudTV. Net revenue = gross − ad server cost.</p>
        </div>
        {readOnly ? (
          <span className="text-xs font-medium text-slate-500 bg-slate-100 px-3 py-1.5 rounded-full" data-testid="ad-costs-readonly-badge">View only</span>
        ) : (
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-slab-btn">
            <Plus size={16} className="mr-1.5" /> Add Slab
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-blue-600" size={24} /></div>
      ) : (
        <div className="space-y-5">
          {grouped.map((g) => (
            <div key={g.value} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`slab-group-${g.value}`}>
              <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                <g.icon size={16} className="text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-900">{g.label}</h3>
                <span className="text-xs text-slate-400 ml-auto">{g.items.length} slab{g.items.length === 1 ? "" : "s"}</span>
              </div>
              {g.items.length === 0 ? (
                <div className="px-5 py-8 text-center text-sm text-slate-400">No cost slabs yet. Add one to enable net-revenue calculation.</div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {g.items.map((c) => (
                    <div key={c.cost_id} className="px-5 py-3 flex items-start justify-between gap-4" data-testid={`slab-row-${c.cost_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <Layers size={14} className="text-blue-500" />
                          <span className="text-sm font-medium text-slate-800">
                            {c.start_date} → {c.end_date || "open-ended"}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {c.tiers.map((t, i) => (
                            <span key={i} className="text-xs bg-slate-50 border border-slate-200 rounded-md px-2 py-1 text-slate-600 font-mono">
                              {fmtNum(t.from_impressions)}–{fmtNum(t.to_impressions)} : ${Number(t.cpm).toFixed(2)} CPM
                            </span>
                          ))}
                        </div>
                      </div>
                      {!readOnly && <div className="flex items-center gap-1 flex-shrink-0">
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => openEdit(c)} data-testid={`edit-slab-${c.cost_id}`}>
                          <Pencil size={14} />
                        </Button>
                        <Button variant="outline" size="sm" className="h-8 w-8 p-0 text-red-500 hover:bg-red-50" onClick={() => setDeleteTarget(c)} data-testid={`delete-slab-${c.cost_id}`}>
                          <Trash2 size={14} />
                        </Button>
                      </div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="slab-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Cost Slab" : "Add Cost Slab"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Ad Server</Label>
              <select
                className="w-full h-9 border border-slate-200 rounded-md text-sm px-3 disabled:bg-slate-50 disabled:text-slate-400"
                value={form.ad_server}
                disabled={!!editing}
                onChange={(e) => setForm({ ...form, ad_server: e.target.value })}
                data-testid="slab-server-select"
              >
                {AD_SERVERS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-9 text-sm" data-testid="slab-start-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-500">End Date <span className="text-slate-300">(blank = open)</span></Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="h-9 text-sm" data-testid="slab-end-date" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-slate-500">Impression Tiers (flat CPM)</Label>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addTier} data-testid="add-tier-btn">
                  <Plus size={12} className="mr-1" /> Tier
                </Button>
              </div>
              <div className="space-y-2">
                {form.tiers.map((t, i) => {
                  const isLast = i === form.tiers.length - 1;
                  return (
                    <div key={i} className="flex items-center gap-2" data-testid={`tier-row-${i}`}>
                      <span className="text-xs text-slate-500 w-20 font-mono text-right">{fmtNum(t.from_impressions)}</span>
                      <span className="text-slate-300 text-xs">→</span>
                      <Input
                        type="number"
                        placeholder={isLast ? "∞ (blank)" : "to"}
                        value={t.to_impressions === null ? "" : t.to_impressions}
                        onChange={(e) => updateTierTo(i, e.target.value)}
                        className="h-8 text-xs w-28"
                        data-testid={`tier-to-${i}`}
                      />
                      <div className="flex items-center gap-1 flex-1">
                        <span className="text-xs text-slate-400">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="CPM"
                          value={t.cpm}
                          onChange={(e) => updateTierCpm(i, e.target.value)}
                          className="h-8 text-xs"
                          data-testid={`tier-cpm-${i}`}
                        />
                      </div>
                      <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-red-500" disabled={form.tiers.length === 1} onClick={() => removeTier(i)} data-testid={`remove-tier-${i}`}>
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Leave the last tier's "to" blank for "and above". CPM = cost per 1,000 impressions.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-slab-btn">
              {saving ? <Loader2 className="animate-spin" size={16} /> : (editing ? "Save Changes" : "Create Slab")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this cost slab?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && `${serverLabel(deleteTarget.ad_server)} · ${deleteTarget.start_date} → ${deleteTarget.end_date || "open"}. Net revenue for this period will become unconfigured until you add a new slab.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-slab">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
